import { type Middleware, isRejectedWithValue } from "@reduxjs/toolkit";
import * as Sentry from "@sentry/react";

import { resolveUserFacingErrorMessage } from "@/app/api/errorMessages";
import { errorMessagesIntl } from "@/app/api/errorMessages.intl";
import { ErrorSource, LOCK_CONFLICT_CODE } from "@/app/constants/constants";
import { GLOBAL_ERROR_MESSAGE } from "@/app/constants/global-constants";
import { ROUTES } from "@/app/constants/routes_sidebar/routes.constant";
import { createTranslator } from "@/app/utils/intlMessage.utils";
import { clearStoredSessionPreservingSubdomain } from "@/app/utils/session";
import { customToast } from "@/components/alerts/CustomToast";

const t = createTranslator(errorMessagesIntl);

interface RejectedAction {
  type: string;
  payload?: {
    status?: number;
    data?: {
      message?: string;
      source?: ErrorSource;
    };
  };
  meta?: {
    arg?: {
      endpointName?: string;
    };
    baseQueryMeta?: {
      request?: {
        method?: string;
        url?: string;
      };
    };
  };
}

export const rtkQueryErrorLogger: Middleware = () => (next) => (action) => {
  if (isRejectedWithValue(action)) {
    const rejectedAction = action as RejectedAction;
    const status = rejectedAction.payload?.status;
    const responseMessage = rejectedAction.payload?.data?.message;
    const endpointName = rejectedAction.meta?.arg?.endpointName ?? "unknown";
    const requestMethod =
      rejectedAction.meta?.baseQueryMeta?.request?.method ?? "GET";

    Sentry.captureMessage(
      `Form/API request failed: ${requestMethod} ${endpointName}`,
      {
        level: status && status >= 500 ? "error" : "warning",
        tags: {
          source: "rtk-query",
          endpoint: endpointName,
          method: requestMethod,
          status: status?.toString() ?? "unknown",
        },
        extra: {
          actionType: rejectedAction.type,
          requestUrl: rejectedAction.meta?.baseQueryMeta?.request?.url,
          responseMessage,
          payload: rejectedAction.payload,
        },
      },
    );

    if (!navigator.onLine) {
      customToast.error(t("fetchError"));
    } else if (
      // @ts-expect-error action payload can be unknown
      action.payload.status === 401 &&
      !window.location.pathname.includes(ROUTES.LOGIN) &&
      !window.location.pathname.includes(ROUTES.THANK_YOU) &&
      !window.location.pathname.includes(ROUTES.TIMESHEET_APPROVAL) &&
      !window.location.pathname.includes(ROUTES.DOCKET_SIGNATURE)
    ) {
      customToast.error(t("sessionExpired"));
      clearStoredSessionPreservingSubdomain();
      window.location.href = ROUTES.LOGIN;
    }

    // @ts-expect-error action payload can be unknown
    else if (action.payload.data) {
      const payload = action.payload as { status?: number };

      if (
        payload?.status === 401 &&
        window.location.pathname.includes(ROUTES.DOCKET_SIGNATURE)
      ) {
        return next(action);
      }

      // @ts-expect-error action payload can be unknown
      const errorCode = action.payload?.data?.meta?.code;

      if (errorCode === LOCK_CONFLICT_CODE) {
        return next(action);
      }

      // @ts-expect-error action payload can be unknown
      const message = action.payload?.data?.message;
      if (
        message !==
        "No reports were found, please create daily report to see the summary."
      ) {
        customToast.error(
          resolveUserFacingErrorMessage(action.payload, GLOBAL_ERROR_MESSAGE),
        );
      }
    } else {
      customToast.error(GLOBAL_ERROR_MESSAGE);
    }
  }

  return next(action);
};
